import React, { useState, useRef, useEffect, useId } from 'react';
import { RiSearchLine, RiArrowDownSLine, RiCloseLine } from 'react-icons/ri';
import { searchKey } from '../../utils/thaiText';

interface SearchableSelectProps {
  options: any[];
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  error?: string;
  optionValue?: string;
  optionLabel?: string;
  searchable?: boolean;
  /** ข้อความเมื่อไม่มีตัวเลือก — ใช้บอกสาเหตุ/ทางแก้แทนคำว่า 'ไม่พบข้อมูล' ลอยๆ */
  emptyMessage?: string;
  /**
   * ข้อความเมื่อ "มีตัวเลือกอยู่ แต่คำค้นไม่ตรงสักรายการ" (โหมดกรองในเครื่อง) — ต่างจาก emptyMessage
   * ที่หมายถึง "ไม่มีตัวเลือกเลย" · ไม่ส่ง = `ไม่พบรายการที่ตรงกับ "<คำค้น>"` (KD3 S2613 BUG-S2613-001:
   * เดิมพิมพ์คำค้นไม่ตรงแล้วขึ้น emptyMessage ของผู้เรียก เช่น "ยังไม่มีอุปกรณ์แบบนับจำนวนในระบบ" ทั้งที่มี)
   */
  noResultsMessage?: string | ((term: string) => string);
  /**
   * ค้นหาฝั่ง server: ใส่มาเมื่อ options ถูกโหลดตามคำค้น (ชุดใหญ่เกินโหลดหมด)
   * — ผู้เรียกรับผิดชอบ debounce เอง · เมื่อใส่ prop นี้ component จะ **ไม่กรอง options เองอีก**
   * เพราะ server กรองมาแล้ว (กรองซ้ำจะตัดผลที่ match ฟิลด์ที่ไม่ได้อยู่ใน label ทิ้ง)
   */
  onSearchChange?: (term: string) => void;
  label?: string;
  allowClear?: boolean;
}


/**
 * BUG-S2103-001: ตัวเลือกมักใช้ id ตัวเลข แต่ค่าที่หน้าเก็บไว้ (state จาก URL / onChange แล้ว .toString())
 * เป็นสตริง — เทียบ `===` แล้วไม่มีตัวไหนตรง ช่องจึงขึ้น placeholder ทั้งที่รายการถูกกรองอยู่
 * (หน้าโครงการ: กรองบริษัทแล้วช่องบริษัทยังขึ้น "เลือกบริษัท") · เทียบแบบสตริงเมื่อชนิดต่างกัน
 */
export const sameOptionValue = (a: unknown, b: unknown): boolean =>
  a === b || (a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b));

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '-- เลือก --',
  searchPlaceholder = 'ค้นหา...',
  className = '',
  disabled = false,
  required = false,
  name,
  error,
  optionValue = 'value',
  optionLabel = 'label',
  searchable = true,
  emptyMessage,
  noResultsMessage,
  label,
  onSearchChange
}) => {
  // BUG-S381-DUPID FIX: the error/listbox/option ids were `${fieldId}-...`.
  // When a form renders ≥2 <SearchableSelect> without a distinct `name` (e.g. DevicePage's
  // device_type + supplier), every instance fell back to the SAME literal 'searchable-select',
  // producing duplicate ids. aria-describedby then resolved to the FIRST instance's error, so a
  // screen reader on the 2nd invalid select announced the wrong field's error (WCAG 4.1.1 dup-id
  // + 3.3.1 error identification). useId() guarantees a unique base per instance; a caller-supplied
  // `name` still wins for readability/back-compat.
  const reactId = useId();
  const fieldId = name || reactId;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // KD3 S2491 (NFR-MD-011): ผู้เรียก 50/64 จุดวาง <label> ไว้ข้างบนแต่ไม่ส่ง prop `label` → ชื่อที่
  // screen reader อ่านกลายเป็น placeholder ("-- เลือกพนักงานขาย --") ไม่ใช่ชื่อช่อง ("พนักงานขายที่ดูแล")
  // · <label> พี่น้องไม่ผูกกับ <button> เอง → เมื่อไม่มี prop ให้หยิบข้อความ label ที่อยู่ติดก่อนหน้า (ตัด * บังคับกรอก)
  const [siblingLabel, setSiblingLabel] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (label) return;
    const prev = dropdownRef.current?.previousElementSibling;
    if (prev?.tagName === 'LABEL') {
      const text = (prev.textContent || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
      setSiblingLabel(text || undefined);
    }
  }, [label]);
  const fieldLabel = label || siblingLabel;

  // Helper function to get value from option
  const getOptionValue = (option: any) => {
    return option?.[optionValue] ?? option?.value ?? option;
  };

  // Helper function to get label from option
  const getOptionLabel = (option: any) => {
    return option?.[optionLabel] ?? option?.label ?? String(option);
  };

  // Ensure options is always an array
  const safeOptions = options || [];

  // Filter options based on search term (รองรับทั้งภาษาไทยและอังกฤษ)
  // โหมด server-search (มี onSearchChange) → ใช้ options ตามที่ server ส่งมาเลย ไม่กรองซ้ำ
  const filteredOptions = onSearchChange ? safeOptions : safeOptions.filter(option => {
    const label = getOptionLabel(option);
    if (!label) return false;

    // ค้นหาแบบไม่สนใจ case และรองรับภาษาไทย · สระอำสองแบบ ("จำกัด" ↔ "จํากัด") เทียบเท่ากัน (utils/thaiText)
    const labelLower = searchKey(label.toString());
    const searchLower = searchKey(searchTerm);

    return labelLower.includes(searchLower);
  });

  // Get selected option label
  const selectedOption = safeOptions.find(opt => sameOptionValue(getOptionValue(opt), value));
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : placeholder;
  
  // Truncate long display values to prevent overflow
  const truncatedDisplayValue = displayValue && displayValue.length > 50 
    ? displayValue.substring(0, 47) + '...' 
    : displayValue;

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(getOptionValue(filteredOptions[highlightedIndex]));
        }
        break;
      case 'Escape':
        e.preventDefault();
        // หยุดไม่ให้ Escape ลอยไปถึง document-level listener ของ Modal (ui/Modal.tsx ใช้
        // document.addEventListener('keydown') ปิด modal ด้วย onClose). มิฉะนั้น Escape ครั้งเดียว
        // ตอน dropdown เปิด จะปิดทั้ง dropdown + modal → ฟอร์มที่กรอกค้างหายทั้งหมด.
        // case นี้ทำงานเฉพาะตอน dropdown เปิด (ดู early-return ด้านบนเมื่อ !isOpen) → เมื่อ dropdown
        // ปิดแล้ว Escape จะ bubble ไปปิด modal ตามปกติ (พฤติกรรม nested-overlay ที่ถูกต้อง).
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setIsOpen(false);
        setSearchTerm('');
        triggerRef.current?.focus();
        break;
    }
  };

  const handleSelect = (selectedValue: string | number) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
    // Return focus to trigger after selection
    triggerRef.current?.focus();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Hidden input for form validation */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value || ''}
        />
      )}
      {/* Main button */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? `${fieldId}-listbox` : undefined}
        aria-label={selectedOption ? (fieldLabel ? `${fieldLabel}: ${displayValue}` : displayValue) : (fieldLabel || placeholder)}
        // BUG-S083-ERRBIND: the error text was rendered below the control but never tied to
        // it, so a screen-reader user landing on the field heard its name and nothing about
        // what was wrong — they had to hunt for the message. Announce it via
        // aria-invalid + aria-describedby.
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        data-name={name}
        className={`
          w-full px-4 min-h-[44px] sm:min-h-[42px] py-2.5 text-left bg-white border rounded-lg h-[42px]
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20 transition-colors
          ${disabled ? 'bg-secondary-50 cursor-not-allowed' : 'hover:border-secondary-400'}
          ${error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500'}
          ${isOpen ? 'ring-2' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <span className={`flex-1 truncate pr-2 ${value ? 'text-secondary-900' : 'text-secondary-500'}`}>
            {truncatedDisplayValue}
          </span>
          <div className="flex items-center flex-shrink-0">
            {value && !disabled && (
              <RiCloseLine
                className="w-4 h-4 mr-1 text-secondary-400 hover:text-secondary-600"
                onClick={handleClear}
              />
            )}
            <RiArrowDownSLine
              className={`w-5 h-5 text-secondary-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {/* Error message */}
      {error && (
        <p
          id={`${fieldId}-error`}
          role="alert"
          className="mt-1 text-sm text-danger-600" /* BUG-S2231-002: danger-500 บนขาว contrast 3.76 < 4.5 (axe) */
        >
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-[9999] w-full mt-1 bg-white border border-secondary-300 rounded-lg shadow-lg"
          style={{
            maxHeight: 'calc(100vh - 200px)',
            transform: 'translateZ(0)' // Force GPU acceleration for better rendering
          }}
        >
          {/* Search input */}
          <div className="p-2 border-b bg-white sticky top-0 z-10">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                ref={searchInputRef}
                type="text"
                // FINDING-S210-ARIADESC: การไล่ option ด้วยลูกศรอัปเดตแค่ highlight ที่มองเห็น
                // (bg-primary-50) แต่ไม่บอก screen reader ว่ากำลังชี้ตัวไหน — ผู้ใช้ SR ที่โฟกัส
                // ช่องค้นหาเลยไม่ได้ยิน option ปัจจุบันเลย (WCAG 4.1.2). ประกาศผ่าน editable-combobox
                // pattern: role=combobox + aria-controls ชี้ listbox + aria-activedescendant ชี้
                // id ของ option ที่ highlight อยู่ (แต่ละ option มี id `${name}-option-${index}`).
                role="combobox"
                aria-expanded={true}
                aria-controls={`${fieldId}-listbox`}
                aria-autocomplete="list"
                aria-activedescendant={
                  filteredOptions.length > 0 && highlightedIndex < filteredOptions.length
                    ? `${fieldId}-option-${highlightedIndex}`
                    : undefined
                }
                aria-label={fieldLabel ? `ค้นหา${fieldLabel}` : searchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setHighlightedIndex(0);
                  // โหมด server-search: บอกผู้เรียกไปโหลด options ชุดใหม่ (ผู้เรียก debounce เอง)
                  onSearchChange?.(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 text-sm border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Options list */}
          <div
            id={`${fieldId}-listbox`}
            role="listbox"
            className="max-h-60 overflow-y-auto overscroll-contain"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optValue = getOptionValue(option);
                const optLabel = getOptionLabel(option);
                return (
                  <button
                    key={optValue}
                    id={`${fieldId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={sameOptionValue(optValue, value)}
                    onClick={() => handleSelect(optValue)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`
                    w-full px-3 py-2 text-left text-sm hover:bg-primary-50 transition-colors
                    ${sameOptionValue(optValue, value) ? 'bg-primary-100 text-primary-900 font-medium' : ''}
                    ${index === highlightedIndex ? 'bg-primary-50' : ''}
                  `}
                  >
                    {optLabel}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-secondary-500 text-center whitespace-pre-line [overflow-wrap:anywhere]">
                {/* BUG-S2613-001: โหมดกรองในเครื่อง + มี options แต่คำค้นไม่ตรง → ต้องบอกว่า "ไม่ตรงคำค้น"
                    ไม่ใช่ emptyMessage ("ยังไม่มีข้อมูลเลย") ซึ่งพาผู้ใช้ไปสร้างของใหม่ทั้งที่มีอยู่แล้ว ·
                    โหมด server (onSearchChange) options = ผลค้นจาก server อยู่แล้ว → ใช้ emptyMessage ตามเดิม */}
                {!onSearchChange && safeOptions.length > 0 && searchTerm.trim()
                  ? (typeof noResultsMessage === 'function'
                      ? noResultsMessage(searchTerm.trim())
                      : noResultsMessage || `ไม่พบรายการที่ตรงกับ "${searchTerm.trim()}"`)
                  : (emptyMessage || 'ไม่พบข้อมูล')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;